import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Meta from '@/components/Meta';

/* ─── Design Tokens ─── */
const GOLD   = '#c9a84c';
const GOLD2  = '#e8c97a';
const NAVY   = '#0c1b44';
const DARK   = '#080d1a';
const CREAM  = '#f5f3ef';
const WHITE  = '#ffffff';

/* ─── Mobile hook ─── */
function useIsMobile(breakpoint = 960) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ─── Utility: Intersection Observer fade-in ─── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, direction = 'up', style = {}, className = '' }) {
  const [ref, visible] = useInView(0.08);
  const transforms = {
    up:    visible ? 'translateY(0)'   : 'translateY(48px)',
    left:  visible ? 'translateX(0)'   : 'translateX(-48px)',
    right: visible ? 'translateX(0)'   : 'translateX(48px)',
    scale: visible ? 'scale(1)'        : 'scale(0.94)',
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: transforms[direction] || transforms.up,
        transition: `opacity 1s cubic-bezier(.16,1,.3,1) ${delay}s, transform 1s cubic-bezier(.16,1,.3,1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Gold accent line ─── */
const GoldLine = ({ w = 48, mb = 24, center = false }) => (
  <div style={{
    width: w, height: 1.5, background: `linear-gradient(90deg, ${GOLD}, ${GOLD2})`,
    marginBottom: mb, ...(center ? { margin: `0 auto ${mb}px` } : {}),
  }} />
);

/* ─── Eyebrow label ─── */
const Eyebrow = ({ children, light = false, center = false }) => (
  <p style={{
    fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase',
    color: light ? 'rgba(255,255,255,0.45)' : '#aaa',
    fontFamily: 'Inter, sans-serif', fontWeight: 500,
    marginBottom: 16,
    textAlign: center ? 'center' : 'left',
  }}>{children}</p>
);

/* ─── Full-screen section heading ─── */
const Heading = ({ children, light = false, size = 'lg', center = false, italic = false }) => {
  const sizes = { sm: 'clamp(28px,3vw,44px)', md: 'clamp(36px,4vw,60px)', lg: 'clamp(44px,5.5vw,80px)', xl: 'clamp(52px,7vw,110px)' };
  return (
    <h2 style={{
      fontSize: sizes[size], fontWeight: 200,
      color: light ? WHITE : NAVY,
      fontFamily: "'Playfair Display', Georgia, serif",
      lineHeight: 1.05, letterSpacing: '-0.01em',
      fontStyle: italic ? 'italic' : 'normal',
      textAlign: center ? 'center' : 'left',
      margin: 0,
    }}>{children}</h2>
  );
};

/* ─── Product / Category hover card ─── */
function CategoryCard({ title, subtitle, image, link, height = 420, numberLabel }) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(link)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer', position: 'relative',
        overflow: 'hidden', height,
        background: '#0a0a0a', display: 'block',
      }}
    >
      <img
        src={image} alt={title}
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%', objectFit: 'cover',
          transform: hovered ? 'scale(1.1)' : 'scale(1.02)',
          transition: 'transform 1.2s cubic-bezier(.16,1,.3,1)',
          filter: hovered ? 'brightness(0.75)' : 'brightness(0.6)',
        }}
      />
      {/* Bottom gradient */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.18) 55%, transparent 100%)',
      }} />
      {/* Top-right number */}
      {numberLabel && (
        <div style={{
          position: 'absolute', top: 20, right: 24,
          fontSize: 11, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.22)',
          fontFamily: 'Inter, sans-serif',
        }}>{numberLabel}</div>
      )}
      {/* Gold side bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: hovered ? 3 : 0, height: '100%',
        background: `linear-gradient(to top, ${GOLD}, transparent)`,
        transition: 'width 0.5s cubic-bezier(.16,1,.3,1)',
      }} />
      {/* Text */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '32px 28px' }}>
        {subtitle && (
          <p style={{
            fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 8, fontFamily: 'Inter, sans-serif',
          }}>{subtitle}</p>
        )}
        <h3 style={{
          fontSize: 'clamp(18px,1.6vw,24px)', fontWeight: 300, color: WHITE,
          fontFamily: "'Playfair Display', Georgia, serif",
          marginBottom: 16, lineHeight: 1.15,
        }}>{title}</h3>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(12px)',
          transition: 'all 0.45s cubic-bezier(.16,1,.3,1)',
        }}>
          <span style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: WHITE, fontFamily: 'Inter, sans-serif' }}>Explore</span>
          <div style={{ flex: 1, maxWidth: 48, height: 1, background: GOLD }} />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.8">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   NAVBAR — Jaquar-style: white with hover mega-menu dropdowns
   + mobile hamburger drawer
═══════════════════════════════════════════════════════════════ */
function LandingNav() {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const isMobile = useIsMobile(960);
  const navigate = useNavigate();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const menuItems = [
    {
      label: 'Products', to: '/products',
      cols: [
        { title: 'MEP Systems',    items: ['Plumbing', 'Electrical', 'HVAC', 'Fire Safety'] },
        { title: 'Sanitaryware',   items: ['Faucets', 'Showers', 'Toilets', 'Bath Tubs'] },
        { title: 'Lighting',       items: ['Indoor Lighting', 'Outdoor', 'Smart Lighting', 'Emergency'] },
        { title: 'Tools & Hardware', items: ['Power Tools', 'Hand Tools', 'Fasteners', 'Safety Gear'] },
      ],
    },
    { label: 'Services',    to: '/services' },
    { label: 'Industries',  to: '/industries' },
    { label: 'About',       to: '/about' },
    { label: "FAQ's",       to: '/faq' },
    { label: 'Contact',     to: '/contact' },
  ];

  const linkColor = '#1a1a1a';

  return (
    <nav
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: '#ffffff',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
      }}
      onMouseLeave={() => !isMobile && setActiveMenu(null)}
    >
      {/* Top utility bar — hidden on mobile */}
      {!isMobile && (
        <div style={{
          height: 36, background: DARK,
          display: 'flex', alignItems: 'center',
        }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', width: '100%', padding: '0 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
              {[
                { icon: '📞', text: '+971 50 399 2163', href: 'tel:+971503992163' },
                { icon: '✉', text: 'sales@gpgt.ae', href: 'mailto:sales@gpgt.ae' },
                { icon: '📍', text: 'Al Qusais 2, Dubai, UAE', href: '#' },
              ].map(({ icon, text, href }) => (
                <a key={text} href={href} style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10 }}>{icon}</span>{text}
                </a>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 20 }}>
              {['WhatsApp', 'Track Order'].map(t => (
                <span key={t} style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main nav row */}
      <div style={{
        maxWidth: 1400, margin: '0 auto',
        padding: isMobile ? '0 20px' : '0 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: isMobile ? 60 : 72,
      }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}
          onClick={() => setMobileOpen(false)}>
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Grand Palace"
            style={{ height: isMobile ? 36 : 46, filter: 'none', objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
          />
          <span style={{ display: 'none', fontFamily: "'Playfair Display', Georgia, serif", fontSize: isMobile ? 18 : 22, fontWeight: 300, color: linkColor, letterSpacing: '0.04em' }}>Grand Palace</span>
        </Link>

        {/* Desktop Nav links */}
        {!isMobile && (
          <ul style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0, gap: 0, alignItems: 'center' }}>
            {menuItems.map(item => (
              <li key={item.label} style={{ position: 'relative' }}
                onMouseEnter={() => item.cols ? setActiveMenu(item.label) : setActiveMenu(null)}
              >
                <Link
                  to={item.to}
                  style={{
                    display: 'block', padding: '0 18px',
                    textDecoration: 'none', lineHeight: '72px',
                    fontSize: 12, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: activeMenu === item.label ? GOLD : linkColor,
                    fontFamily: 'Inter, sans-serif', fontWeight: 500,
                    transition: 'color 0.3s ease',
                    borderBottom: activeMenu === item.label ? `2px solid ${GOLD}` : '2px solid transparent',
                  }}
                >
                  {item.label}
                </Link>

                {/* Mega dropdown */}
                {item.cols && (
                  <div style={{
                    position: 'fixed', left: 0, right: 0,
                    top: 108,
                    background: WHITE,
                    boxShadow: '0 32px 80px rgba(0,0,0,0.12)',
                    borderTop: `2px solid ${GOLD}`,
                    opacity: activeMenu === item.label ? 1 : 0,
                    pointerEvents: activeMenu === item.label ? 'auto' : 'none',
                    transform: activeMenu === item.label ? 'translateY(0)' : 'translateY(-12px)',
                    transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                    zIndex: 999,
                  }}>
                    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '40px 40px', display: 'grid', gridTemplateColumns: `repeat(${item.cols.length}, 1fr)`, gap: 40 }}>
                      {item.cols.map(col => (
                        <div key={col.title}>
                          <p style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, marginBottom: 16, fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{col.title}</p>
                          {col.items.map(sub => (
                            <Link key={sub} to={`/products?search=${encodeURIComponent(sub)}`}
                              style={{ display: 'block', marginBottom: 10, fontSize: 14, color: '#555', textDecoration: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 300, transition: 'color 0.2s' }}
                              onMouseEnter={e => { e.target.style.color = NAVY; e.target.style.paddingLeft = '8px'; }}
                              onMouseLeave={e => { e.target.style.color = '#555'; e.target.style.paddingLeft = '0'; }}
                            >{sub}</Link>
                          ))}
                        </div>
                      ))}
                      {/* Right promo panel */}
                      <div style={{ background: NAVY, padding: 28, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: 140 }}>
                        <p style={{ fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>Featured</p>
                        <p style={{ fontSize: 16, fontWeight: 200, color: WHITE, fontFamily: "'Playfair Display', serif", marginBottom: 16, lineHeight: 1.3 }}>10,000+ Products<br /><em>In Stock</em></p>
                        <button onClick={() => { navigate('/products'); setActiveMenu(null); }}
                          style={{ background: GOLD, color: NAVY, border: 'none', padding: '10px 20px', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontWeight: 700, width: 'fit-content' }}>
                          Browse All
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Desktop Right actions */}
        {!isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, transition: 'color 0.3s', padding: 4 }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = linkColor}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
              </svg>
            </button>
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 500, transition: 'color 0.3s', padding: 4 }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = linkColor}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              Login
            </button>
            <button onClick={() => navigate('/products')}
              style={{ padding: '10px 26px', background: GOLD, color: NAVY, border: 'none', cursor: 'pointer', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 700, transition: 'all 0.3s' }}
              onMouseEnter={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.color = WHITE; }}
              onMouseLeave={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = NAVY; }}
            >
              Shop Now
            </button>
          </div>
        ) : (
          /* Mobile: Login icon + Hamburger */
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: linkColor, padding: 6 }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </button>
            <button
              onClick={() => setMobileOpen(o => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                width: 36, height: 36, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 5, padding: 4,
              }}
            >
              <span style={{
                display: 'block', width: 22, height: 1.5, background: mobileOpen ? GOLD : linkColor,
                transition: 'all 0.3s',
                transform: mobileOpen ? 'translateY(6.5px) rotate(45deg)' : 'none',
              }} />
              <span style={{
                display: 'block', width: 22, height: 1.5, background: mobileOpen ? GOLD : linkColor,
                transition: 'all 0.3s',
                opacity: mobileOpen ? 0 : 1,
              }} />
              <span style={{
                display: 'block', width: 22, height: 1.5, background: mobileOpen ? GOLD : linkColor,
                transition: 'all 0.3s',
                transform: mobileOpen ? 'translateY(-6.5px) rotate(-45deg)' : 'none',
              }} />
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile Drawer ── */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 60, left: 0, right: 0, bottom: 0,
          background: WHITE, zIndex: 998,
          transform: mobileOpen ? 'translateX(0)' : 'translateX(100%)',
          visibility: mobileOpen ? 'visible' : 'hidden',
          transition: 'transform 0.38s cubic-bezier(.16,1,.3,1), visibility 0.38s',
          overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehaviorY: 'contain',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Contact strip */}
          <div style={{ background: DARK, padding: '14px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <a href="tel:+971503992163" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>📞</span> +971 50 399 2163
            </a>
            <a href="mailto:sales@gpgt.ae" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>✉</span> sales@gpgt.ae
            </a>
          </div>

          {/* Nav items */}
          <div style={{ flex: 1, padding: '8px 0' }}>
            {menuItems.map((item, idx) => (
              <div key={item.label}>
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px',
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (item.cols) {
                      setMobileExpanded(mobileExpanded === item.label ? null : item.label);
                    } else {
                      navigate(item.to);
                      setMobileOpen(false);
                    }
                  }}
                >
                  <span style={{ fontSize: 14, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 500, color: linkColor }}>
                    {item.label}
                  </span>
                  {item.cols && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2"
                      style={{ transform: mobileExpanded === item.label ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}>
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  )}
                </div>
                {/* Expandable sub-items */}
                {item.cols && mobileExpanded === item.label && (
                  <div style={{ background: '#fafafa', padding: '12px 24px 20px' }}>
                    {item.cols.map(col => (
                      <div key={col.title} style={{ marginBottom: 16 }}>
                        <p style={{ fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: GOLD, marginBottom: 10, fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{col.title}</p>
                        {col.items.map(sub => (
                          <Link key={sub} to={`/products?search=${encodeURIComponent(sub)}`}
                            style={{ display: 'block', marginBottom: 8, fontSize: 14, color: '#666', textDecoration: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
                            onClick={() => setMobileOpen(false)}
                          >{sub}</Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile CTA buttons */}
          <div style={{ padding: '20px 24px 32px', display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <button onClick={() => { navigate('/products'); setMobileOpen(false); }}
              style={{ width: '100%', padding: '14px', background: GOLD, color: NAVY, border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 700 }}
            >Shop Now</button>
            <button onClick={() => { navigate('/contact'); setMobileOpen(false); }}
              style={{ width: '100%', padding: '14px', background: 'transparent', color: NAVY, border: `1px solid ${NAVY}`, cursor: 'pointer', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
            >Request a Quote</button>
            <a href="https://wa.me/971503992163" target="_blank" rel="noopener noreferrer"
              style={{ width: '100%', padding: '14px', background: '#25D366', color: WHITE, border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 600, textAlign: 'center', textDecoration: 'none', display: 'block', boxSizing: 'border-box' }}
            >💬 WhatsApp Us</a>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO — Full-screen cinematic carousel (Jaquar style)
═══════════════════════════════════════════════════════════════ */
function HeroSection() {
  const navigate = useNavigate();
  const [idx, setIdx]     = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [prog, setProg]   = useState(0);
  const progRef = useRef(null);
  const SLIDE_MS = 6000;

  const slides = [
    {
      image: '/media/hero_banner.jpg',
      label: 'Precision · Durability · Comfort',
      headline: ['High Performance', 'Products.'],
      sub: 'Designed for Durability, Hygiene and Comfort — trusted across the UAE.',
      cta1: 'Explore Collection', cta2: 'Request a Quote',
      align: 'left',
    },
    {
      image: '/media/banner_categories1.jpg',
      label: 'MEP Systems',
      headline: ['Engineering', 'Excellence.'],
      sub: 'Complete MEP supply — from plumbing to high-voltage electrical, all under one roof in Dubai.',
      cta1: 'View MEP Range', cta2: 'Get a Quote',
      align: 'left',
    },
    {
      image: '/media/banner_categories2.jpg',
      label: 'Tools & Hardware',
      headline: ['Built for', 'Professionals.'],
      sub: 'The UAE\'s broadest catalogue of power tools, hardware, and construction essentials.',
      cta1: 'Shop Tools', cta2: 'Contact Team',
      align: 'left',
    },
  ];

  const resetProgress = useCallback(() => {
    setProg(0);
    clearInterval(progRef.current);
    const start = Date.now();
    progRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min((elapsed / SLIDE_MS) * 100, 100);
      setProg(p);
      if (p >= 100) clearInterval(progRef.current);
    }, 50);
  }, []);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    resetProgress();
    return () => clearInterval(progRef.current);
  }, [resetProgress]);

  useEffect(() => {
    const t = setTimeout(() => {
      setIdx(p => (p + 1) % slides.length);
    }, SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx, slides.length]);

  useEffect(() => { resetProgress(); }, [idx, resetProgress]);

  const s = slides[idx];

  const isMobile = useIsMobile();

  return (
    <section style={{ position: 'relative', width: '100%', height: '100svh', minHeight: isMobile ? 560 : 700, overflow: 'hidden', background: DARK }}>
      {/* Slides */}
      {slides.map((sl, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          opacity: i === idx ? 1 : 0,
          transition: 'opacity 1.4s cubic-bezier(.4,0,.2,1)',
          zIndex: 1,
        }}>
          <img
            src={sl.image} alt={sl.label}
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: i === idx ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 7s ease',
              filter: 'brightness(0.38)',
            }}
            onError={e => { e.target.style.background = DARK; }}
          />
        </div>
      ))}

      {/* Cinematic overlays */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, background: 'linear-gradient(105deg, rgba(8,13,26,0.92) 0%, rgba(8,13,26,0.55) 55%, rgba(8,13,26,0.1) 100%)' }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 2, background: 'linear-gradient(to top, rgba(8,13,26,0.85) 0%, transparent 40%)' }} />

      {/* Side decorative line — desktop only */}
      {!isMobile && (
        <div style={{ position: 'absolute', left: 36, top: '15%', bottom: '15%', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
          <div style={{ width: 1, flex: 1, background: `linear-gradient(to bottom, transparent, ${GOLD}60, transparent)` }} />
        </div>
      )}

      {/* Hero content */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 10,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: isMobile ? '0 6vw' : '0 9vw',
        maxWidth: isMobile ? '100%' : 1000,
        opacity: loaded ? 1 : 0, transition: 'opacity 1s ease',
      }}>
        {/* Label */}
        <div key={idx + '-label'} style={{
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: isMobile ? 20 : 36,
          animation: 'heroSlideIn 0.8s cubic-bezier(.16,1,.3,1) forwards',
          opacity: 0,
        }}>
          <div style={{ width: isMobile ? 32 : 52, height: 1, background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
          <span style={{ fontSize: isMobile ? 9 : 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD, fontFamily: 'Inter, sans-serif' }}>{s.label}</span>
        </div>

        {/* Headline */}
        <h1 key={idx + '-h1'} style={{
          fontSize: isMobile ? 'clamp(36px, 11vw, 60px)' : 'clamp(56px, 8.5vw, 120px)',
          fontWeight: 200, color: WHITE,
          fontFamily: "'Playfair Display', Georgia, serif",
          lineHeight: 0.98, letterSpacing: '-0.02em',
          marginBottom: isMobile ? 18 : 32,
          animation: 'heroSlideIn 0.9s cubic-bezier(.16,1,.3,1) 0.1s forwards',
          opacity: 0,
        }}>
          {s.headline[0]}<br />
          <em style={{ fontStyle: 'italic', color: GOLD2 }}>{s.headline[1]}</em>
        </h1>

        {/* Sub */}
        <p key={idx + '-sub'} style={{
          fontSize: isMobile ? 13 : 'clamp(14px,1.2vw,17px)',
          color: 'rgba(255,255,255,0.6)',
          fontWeight: 300, maxWidth: isMobile ? '90%' : 480, lineHeight: 1.75,
          marginBottom: isMobile ? 32 : 52, fontFamily: 'Inter, sans-serif',
          animation: 'heroSlideIn 0.9s cubic-bezier(.16,1,.3,1) 0.2s forwards',
          opacity: 0,
        }}>{s.sub}</p>

        {/* CTAs */}
        <div key={idx + '-cta'} style={{
          display: 'flex', gap: isMobile ? 10 : 16,
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          animation: 'heroSlideIn 0.9s cubic-bezier(.16,1,.3,1) 0.3s forwards',
          opacity: 0,
        }}>
          <button onClick={() => navigate('/products')}
            style={{ padding: isMobile ? '13px 0' : '15px 44px', width: isMobile ? '100%' : 'auto', background: GOLD, color: NAVY, border: 'none', cursor: 'pointer', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 700, transition: 'all 0.35s' }}
            onMouseEnter={e => { e.currentTarget.style.background = WHITE; }}
            onMouseLeave={e => { e.currentTarget.style.background = GOLD; }}
          >{s.cta1}</button>
          <button onClick={() => navigate('/contact')}
            style={{ padding: isMobile ? '13px 0' : '15px 44px', width: isMobile ? '100%' : 'auto', background: 'transparent', color: WHITE, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 400, transition: 'all 0.35s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.color = WHITE; }}
          >{s.cta2}</button>
        </div>
      </div>

      {/* Slide navigation — progress bars */}
      <div style={{ position: 'absolute', bottom: isMobile ? 24 : 44, left: isMobile ? '6vw' : '9vw', zIndex: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', display: 'flex', flexDirection: 'column', gap: 4, width: i === idx ? (isMobile ? 48 : 80) : (isMobile ? 18 : 28) }}>
            <div style={{ width: '100%', height: 1.5, background: 'rgba(255,255,255,0.2)', borderRadius: 2, overflow: 'hidden', transition: 'width 0.5s' }}>
              {i === idx && (
                <div style={{ height: '100%', background: GOLD, width: `${prog}%`, transition: 'width 0.05s linear' }} />
              )}
              {i !== idx && idx > i && (
                <div style={{ height: '100%', background: 'rgba(255,255,255,0.45)', width: '100%' }} />
              )}
            </div>
            <span style={{ fontSize: 9, color: i === idx ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', letterSpacing: '0.1em' }}>{String(i + 1).padStart(2, '0')}</span>
          </button>
        ))}
      </div>

      {/* Scroll cue — desktop only */}
      {!isMobile && (
        <div style={{ position: 'absolute', bottom: 44, right: 44, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif', writingMode: 'vertical-rl' }}>Scroll</span>
          <div style={{ width: 1, height: 56, background: `linear-gradient(to bottom, transparent, ${GOLD}80)`, animation: 'scrollPulse 2s ease-in-out infinite' }} />
        </div>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   BRAND MARQUEE — scrolling brand logos / names
═══════════════════════════════════════════════════════════════ */
function BrandMarquee() {
  const brands = ['Jaquar', 'Ducab', 'Havells', 'Hilti', 'Bosch', 'Legrand', 'Clipsal', 'Honeywell', 'Tyco', 'Jotun', 'Milwaukee', 'Stanley', 'RAK Ceramics', 'Knauf', 'Granlace'];
  return (
    <div style={{ background: DARK, padding: '22px 0', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div style={{ display: 'flex', gap: 0, animation: 'marquee 40s linear infinite', width: 'max-content' }}>
        {[...brands, ...brands].map((b, i) => (
          <React.Fragment key={i}>
            <span style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.22)', fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', padding: '0 36px' }}>{b}</span>
            <span style={{ color: `${GOLD}30`, fontSize: 10 }}>✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   IDENTITY SPLIT — "Who We Are" editorial 50/50
═══════════════════════════════════════════════════════════════ */
function IdentitySection() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  return (
    <section style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', minHeight: isMobile ? 'auto' : 660 }}>
      {/* Image */}
      <div style={{ position: 'relative', overflow: 'hidden', height: isMobile ? 280 : 'auto' }}>
        <img
          src="/media/hero_brand_story.png"
          alt="Grand Palace Showroom"
          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 1.2s cubic-bezier(.16,1,.3,1)' }}
          onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          onError={e => { e.target.style.background = '#111'; }}
        />
        {/* Gold corner accent */}
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: isMobile ? 56 : 88, height: isMobile ? 56 : 88, background: GOLD, opacity: 0.9 }} />
        {!isMobile && <div style={{ position: 'absolute', bottom: 88, right: 0, width: 88, height: 88, border: `1px solid ${GOLD}40` }} />}
        {/* Year badge */}
        <div style={{
          position: 'absolute', top: isMobile ? 16 : 32, left: isMobile ? 16 : 32,
          background: 'rgba(8,13,26,0.85)', backdropFilter: 'blur(12px)',
          padding: isMobile ? '10px 14px' : '16px 22px', border: `1px solid ${GOLD}30`,
        }}>
          <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 200, color: GOLD, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>20+</div>
          <div style={{ fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif', marginTop: 4 }}>Years of Excellence</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ background: CREAM, display: 'flex', alignItems: 'center', padding: isMobile ? '48px 24px' : '80px 72px' }}>
        <Reveal style={{ width: '100%' }}>
          <GoldLine mb={20} />
          <Eyebrow>Our Heritage</Eyebrow>
          <Heading size="md">
            Craftsmanship Meets<br />
            <em style={{ fontStyle: 'italic' }}>Innovation.</em>
          </Heading>
          <p style={{ fontSize: 15, color: '#777', lineHeight: 1.95, fontWeight: 300, maxWidth: 420, margin: '28px 0 44px', fontFamily: 'Inter, sans-serif' }}>
            Grand Palace General Trading brings together the world's most trusted
            brands under one roof — from precision MEP systems to industrial safety gear.
            Every product is chosen for exceptional quality, reliability, and long-term value
            for projects across the UAE and beyond.
          </p>

          {/* Mini stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '20px 24px' : '28px 40px', marginBottom: isMobile ? 36 : 48 }}>
            {[
              { n: '10,000+', l: 'SKUs in Catalogue' },
              { n: '50+',    l: 'Trusted Brands' },
              { n: '7',      l: 'Emirates Served' },
              { n: 'ISO',    l: 'Certified Quality' },
            ].map(({ n, l }) => (
              <div key={l}>
                <div style={{ fontSize: 'clamp(26px,2.5vw,36px)', fontWeight: 200, color: NAVY, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>{n}</div>
                <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#aaa', fontFamily: 'Inter, sans-serif', marginTop: 6 }}>{l}</div>
              </div>
            ))}
          </div>

          <button onClick={() => navigate('/about')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 14, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: NAVY, fontFamily: 'Inter, sans-serif', fontWeight: 700, transition: 'color 0.3s' }}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = NAVY}
          >
            Discover Our Story
            <svg width="22" height="8" viewBox="0 0 22 8" fill="none">
              <path d="M0 4h20M17 1l3 3-3 3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STATS BAND
═══════════════════════════════════════════════════════════════ */
function StatsBand() {
  const isMobile = useIsMobile();
  const stats = [
    { number: '756+',  label: 'Products in Stock',   sub: 'Ready to dispatch' },
    { number: '20+',   label: 'Years of Excellence', sub: 'Since 2004, Dubai' },
    { number: '50+',   label: 'Trusted Brands',      sub: 'Global manufacturers' },
    { number: '1,000+',label: 'Projects Delivered',  sub: 'Across the UAE' },
  ];
  return (
    <div style={{ background: NAVY, padding: isMobile ? '48px 6vw' : '80px 8vw', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '1px' : 2, position: 'relative', overflow: 'hidden' }}>
      {/* Decorative circle */}
      <div style={{ position: 'absolute', right: -160, top: '50%', transform: 'translateY(-50%)', width: 520, height: 520, border: `1px solid rgba(201,168,76,0.07)`, borderRadius: '50%', pointerEvents: 'none' }} />

      {stats.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1}>
          <div style={{ textAlign: 'center', padding: isMobile ? '24px 12px' : '32px 16px', borderRight: isMobile ? (i % 2 === 0 ? '1px solid rgba(255,255,255,0.06)' : 'none') : (i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none'), borderBottom: isMobile && i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <div style={{ fontSize: isMobile ? 'clamp(32px,9vw,48px)' : 'clamp(40px,4.5vw,64px)', fontWeight: 200, color: GOLD, fontFamily: "'Playfair Display', serif", lineHeight: 1, marginBottom: 8 }}>{s.number}</div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif', letterSpacing: '0.04em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}>{s.sub}</div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   COLLECTIONS GRID — editorial category showcase
═══════════════════════════════════════════════════════════════ */
function CollectionsGrid({ title, eyebrow, dark = false, categories, reversed = false }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  return (
    <section style={{ padding: isMobile ? '60px 0 60px' : '120px 8vw', background: dark ? '#0e0e0e' : WHITE }}>
      <Reveal>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: isMobile ? 32 : 64, flexWrap: 'wrap', gap: 16, padding: isMobile ? '0 6vw' : 0 }}>
          <div>
            <GoldLine mb={18} />
            <Eyebrow light={dark}>{eyebrow}</Eyebrow>
            <Heading size={isMobile ? 'sm' : 'md'} light={dark}>{title}</Heading>
          </div>
          <button onClick={() => navigate('/products')}
            style={{ background: 'none', border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : NAVY}`, cursor: 'pointer', padding: '10px 24px', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: dark ? 'rgba(255,255,255,0.6)' : NAVY, fontFamily: 'Inter, sans-serif', transition: 'all 0.35s', alignSelf: 'flex-end' }}
            onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = NAVY; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = dark ? 'rgba(255,255,255,0.18)' : NAVY; e.currentTarget.style.color = dark ? 'rgba(255,255,255,0.6)' : NAVY; }}
          >
            View All
          </button>
        </div>
      </Reveal>

      {isMobile ? (
        /* Mobile: horizontal scroll strip */
        <div className="mobile-scroll" style={{ display: 'flex', overflowX: 'auto', gap: 12, padding: '0 6vw 4px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {categories.map((cat, i) => (
            <div key={cat.title} style={{ flex: '0 0 75vw', scrollSnapAlign: 'center' }}>
              <CategoryCard {...cat} height={320} numberLabel={String(i + 1).padStart(2, '0')} />
            </div>
          ))}
          {/* Spacer for right edge */}
          <div style={{ flex: '0 0 2vw' }} />
        </div>
      ) : (
        /* Desktop: Jaquar-style asymmetric grid */
        <div style={{
          display: 'grid',
          gridTemplateColumns: reversed ? '1fr 1fr 1.6fr' : '1.6fr 1fr 1fr',
          gridTemplateRows: '420px 420px',
          gap: 3,
        }}>
          {reversed ? (
            <>
              {categories.slice(1).map((cat, i) => (
                <Reveal key={cat.title} delay={i * 0.08} style={{ height: '100%' }}>
                  <CategoryCard {...cat} height={420} numberLabel={`0${i + 2}`} />
                </Reveal>
              ))}
              <div style={{ gridRow: '1 / 3', gridColumn: 3 }}>
                <Reveal delay={0.28} style={{ height: '100%' }}>
                  <CategoryCard {...categories[0]} height={843} numberLabel="01" />
                </Reveal>
              </div>
            </>
          ) : (
            <>
              <div style={{ gridRow: '1 / 3' }}>
                <Reveal delay={0} style={{ height: '100%' }}>
                  <CategoryCard {...categories[0]} height={843} numberLabel="01" />
                </Reveal>
              </div>
              {categories.slice(1).map((cat, i) => (
                <Reveal key={cat.title} delay={(i + 1) * 0.08} style={{ height: '100%' }}>
                  <CategoryCard {...cat} height={420} numberLabel={`0${i + 2}`} />
                </Reveal>
              ))}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FULL-BLEED EDITORIAL BANNER
═══════════════════════════════════════════════════════════════ */
function EditorialBanner({ image, eyebrow, headline, body, cta, ctaLink, light = true }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  return (
    <section style={{ position: 'relative', height: isMobile ? 'auto' : 600, minHeight: isMobile ? 320 : 'auto', overflow: 'hidden' }}>
      <img src={image} alt={headline}
        style={{ position: isMobile ? 'relative' : 'absolute', inset: 0, width: '100%', height: isMobile ? 220 : '100%', objectFit: 'cover', filter: 'brightness(0.25)', display: 'block' }}
        onError={e => { e.target.style.background = DARK; }}
      />
      {!isMobile && <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, ${DARK}f0 0%, ${DARK}80 50%, ${DARK}10 100%)` }} />}
      <div style={{
        position: isMobile ? 'relative' : 'absolute',
        top: isMobile ? 'auto' : '50%',
        left: isMobile ? 'auto' : '8vw',
        transform: isMobile ? 'none' : 'translateY(-50%)',
        maxWidth: isMobile ? '100%' : 580,
        zIndex: 10,
        padding: isMobile ? '36px 6vw 44px' : 0,
        background: isMobile ? DARK : 'none',
      }}>
        <Reveal>
          <GoldLine mb={18} />
          <Eyebrow light>{eyebrow}</Eyebrow>
          <h2 style={{ fontSize: isMobile ? 'clamp(28px,8vw,44px)' : 'clamp(36px,5vw,68px)', fontWeight: 200, color: WHITE, fontFamily: "'Playfair Display', serif", lineHeight: 1.05, marginBottom: isMobile ? 14 : 22 }}
            dangerouslySetInnerHTML={{ __html: headline }} />
          <p style={{ fontSize: isMobile ? 14 : 15, color: 'rgba(255,255,255,0.58)', lineHeight: 1.85, maxWidth: 440, marginBottom: isMobile ? 28 : 40, fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>{body}</p>
          <button onClick={() => navigate(ctaLink)}
            style={{ padding: isMobile ? '12px 32px' : '14px 40px', background: 'none', border: `1px solid ${GOLD}`, color: GOLD, cursor: 'pointer', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', transition: 'all 0.35s' }}
            onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = NAVY; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = GOLD; }}
          >{cta}</button>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   WHY CHOOSE US — icon feature strip
═══════════════════════════════════════════════════════════════ */
function WhyUsSection() {
  const cats = [
    { image: '/media/category_sanitaryware.png', label: 'BATH · LUXURY',   title: 'Sanitaryware'    },
    { image: '/media/category_plumbing.png',     label: 'MEP · WATER',     title: 'Plumbing'        },
    { image: '/media/category_electrical.png',   label: 'MEP · POWER',     title: 'Electrical'      },
    { image: '/media/category_lighting.png',     label: 'MEP · LIGHT',     title: 'Indoor Lighting' },
    { image: '/media/category_safety.png',       label: 'PROTECTION',      title: 'Safety Equipment'},
    { image: '/media/category_power_tools.png',  label: 'PRECISION ENG.',  title: 'Power Tools'     },
  ];
  const isMobile = useIsMobile();
  return (
    <section style={{ background: DARK, padding: isMobile ? '60px 0 0' : '80px 0 0' }}>
      <div style={{ padding: isMobile ? '0 6vw 40px' : '0 8vw 56px', textAlign: 'center' }}>
        <Reveal>
          <GoldLine mb={16} center />
          <Eyebrow center style={{ color: GOLD }}>Our Categories</Eyebrow>
          <Heading size={isMobile ? 'sm' : 'md'} center style={{ color: WHITE }}>
            Everything You Need,<br />
            <em style={{ fontStyle: 'italic', color: GOLD }}>All in One Place.</em>
          </Heading>
        </Reveal>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: isMobile ? 2 : 0 }}>
        {cats.map((cat, i) => (
          <CategoryCard
            key={cat.title}
            image={cat.image}
            title={cat.title}
            subtitle={cat.label}
            height={isMobile ? 200 : 320}
            numberLabel={String(i + 1).padStart(2, '0')}
            link={`/products?search=${cat.title.toLowerCase()}`}
          />
        ))}
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════════════════
   HORIZONTAL BRAND SHOWCASE
═══════════════════════════════════════════════════════════════ */
function BrandsSection() {
  const isMobile = useIsMobile();
  const brands = ['Jaquar', 'Ducab', 'Havells', 'Hilti', 'Bosch', 'Legrand', 'Honeywell', 'Tyco', 'Jotun', 'RAK Ceramics', 'Milwaukee', 'Knauf'];
  return (
    <section style={{ background: DARK, padding: isMobile ? '56px 6vw' : '88px 8vw' }}>
      <Reveal>
        <GoldLine mb={18} center />
        <Eyebrow light center>Trusted Partners</Eyebrow>
        <Heading size="sm" light center>The World's Best Brands,<br /><em style={{ fontStyle: 'italic', color: GOLD2 }}>All in One Place.</em></Heading>
      </Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: 2, marginTop: isMobile ? 40 : 64 }}>
        {brands.map((b, i) => (
          <Reveal key={b} delay={i * 0.04}>
            <div
              style={{ padding: isMobile ? '20px 8px' : '28px 16px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', transition: 'all 0.35s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD + '50'; e.currentTarget.style.background = 'rgba(201,168,76,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 500, color: 'rgba(255,255,255,0.38)', fontFamily: 'Inter, sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{b}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CTA — Dark luxury call-to-action
═══════════════════════════════════════════════════════════════ */
function CTASection() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  return (
    <section style={{ background: NAVY, padding: isMobile ? '80px 6vw' : '160px 8vw', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      {/* Decorative rings */}
      {[400, 700, 1000].map(d => (
        <div key={d} style={{ position: 'absolute', width: d, height: d, border: `1px solid rgba(201,168,76,${d === 400 ? 0.14 : d === 700 ? 0.07 : 0.03})`, borderRadius: '50%', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }} />
      ))}
      <Reveal>
        <GoldLine mb={20} center />
        <Eyebrow light center>Ready to Build</Eyebrow>
        <h2 style={{ fontSize: 'clamp(44px,6vw,88px)', fontWeight: 200, color: WHITE, fontFamily: "'Playfair Display', serif", lineHeight: 1.0, marginBottom: 24 }}>
          Supply Your Next<br />
          <em style={{ fontStyle: 'italic', color: GOLD2 }}>Great Project.</em>
        </h2>
        <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', lineHeight: 1.9, maxWidth: 520, margin: '0 auto 56px', fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
          From a single fitting to a full-site supply contract, Grand Palace delivers
          premium materials on time, every time. Talk to our specialist team today.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/contact')}
            style={{ padding: '16px 52px', background: GOLD, color: NAVY, border: 'none', cursor: 'pointer', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 700, transition: 'all 0.35s' }}
            onMouseEnter={e => e.currentTarget.style.background = WHITE}
            onMouseLeave={e => e.currentTarget.style.background = GOLD}
          >Contact Our Team</button>
          <button onClick={() => navigate('/products')}
            style={{ padding: '16px 52px', background: 'transparent', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', fontWeight: 400, transition: 'all 0.35s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
          >Browse Catalogue</button>
        </div>
      </Reveal>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FOOTER — editorial dark footer
═══════════════════════════════════════════════════════════════ */
function Footer() {
  const isMobile = useIsMobile();
  const [expandedCol, setExpandedCol] = useState(null);
  const cols = [
    { heading: 'Products',  links: [['Sanitaryware', '/products?search=sanitaryware'], ['Plumbing', '/products?search=plumbing'], ['Electrical', '/products?search=electrical'], ['Power Tools', '/products?search=power+tools'], ['Safety Gear', '/products?search=safety'], ['Lighting', '/products?search=lighting']] },
    { heading: 'Company',   links: [['About Us', '/about'], ['Services', '/services'], ['Industries', '/industries'], ["FAQ's", '/faq'], ['Contact', '/contact']] },
    { heading: 'Portal',    links: [['Login', '/login'], ['Register', '/register'], ['Admin', '/gpgt-portal'], ['Track Order', '/account'], ['Enquiries', '/enquiries']] },
  ];
  return (
    <footer style={{ background: '#060b16', padding: isMobile ? '48px 6vw 32px' : '72px 8vw 40px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
        gap: isMobile ? 0 : 56,
        marginBottom: isMobile ? 0 : 64,
        borderBottom: isMobile ? 'none' : '1px solid rgba(255,255,255,0.05)',
        paddingBottom: isMobile ? 0 : 64,
      }}>
        {/* Brand */}
        <div style={{ marginBottom: isMobile ? 32 : 0 }}>
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Grand Palace"
            style={{ height: 36, marginBottom: 20, filter: 'brightness(0) invert(1) opacity(0.8)', objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.85, fontFamily: 'Inter, sans-serif', maxWidth: 260, fontWeight: 300, marginBottom: 20 }}>
            Shop No:3 Al Sajaya Building,<br />
            Al Qusais 2, Damascus St. 244,<br />
            Dubai, UAE
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { href: 'tel:+971503992163', t: '+971 50 399 2163' },
              { href: 'mailto:sales@gpgt.ae', t: 'sales@gpgt.ae' },
            ].map(({ href, t }) => (
              <a key={t} href={href} style={{ fontSize: 12, color: GOLD, textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'opacity 0.3s' }}
                onMouseEnter={e => e.target.style.opacity = '0.7'}
                onMouseLeave={e => e.target.style.opacity = '1'}
              >{t}</a>
            ))}
          </div>
        </div>
        {/* Link columns */}
        {cols.map(col => (
          <div key={col.heading} style={{ borderTop: isMobile ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            {isMobile ? (
              /* Accordion for mobile */
              <>
                <button
                  onClick={() => setExpandedCol(expandedCol === col.heading ? null : col.heading)}
                  style={{
                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 0', fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase',
                    color: GOLD, fontFamily: 'Inter, sans-serif', fontWeight: 600,
                  }}
                >
                  {col.heading}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2"
                    style={{ transform: expandedCol === col.heading ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s', flexShrink: 0 }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {expandedCol === col.heading && (
                  <div style={{ paddingBottom: 16 }}>
                    {col.links.map(([label, to]) => (
                      <div key={label} style={{ marginBottom: 12 }}>
                        <Link to={to} style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>{label}</Link>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Desktop layout */
              <>
                <p style={{ fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 24, fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{col.heading}</p>
                {col.links.map(([label, to]) => (
                  <div key={label} style={{ marginBottom: 14 }}>
                    <Link to={to} style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 300, transition: 'color 0.3s' }}
                      onMouseEnter={e => e.target.style.color = WHITE}
                      onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.38)'}
                    >{label}</Link>
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: isMobile ? 24 : 0, marginTop: isMobile ? 24 : 0, display: 'flex', justifyContent: isMobile ? 'center' : 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, flexDirection: isMobile ? 'column' : 'row', textAlign: isMobile ? 'center' : 'left' }}>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif' }}>
          © {new Date().getFullYear()} Grand Palace General Trading LLC. All rights reserved.
        </p>
        <div style={{ display: 'flex', gap: isMobile ? 16 : 32, flexWrap: 'wrap', justifyContent: 'center' }}>
          {['Privacy Policy', 'Terms of Use', 'TRN: 100568910400003'].map(t => (
            <span key={t} style={{ fontSize: 10, color: 'rgba(255,255,255,0.18)', fontFamily: 'Inter, sans-serif', letterSpacing: '0.06em', cursor: 'default' }}>{t}</span>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FLOATING WIDGETS — WhatsApp + Chat
═══════════════════════════════════════════════════════════════ */
function FloatingWidgets() {
  return (
    <div style={{ position: 'fixed', bottom: 28, right: 24, zIndex: 900, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-end' }}>
      <a href="https://wa.me/971503992163" target="_blank" rel="noopener noreferrer"
        style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#25D366', color: WHITE, padding: '10px 18px', textDecoration: 'none', fontSize: 12, fontFamily: 'Inter, sans-serif', fontWeight: 600, boxShadow: '0 4px 20px rgba(37,211,102,0.4)', transition: 'all 0.3s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        WhatsApp
      </a>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN LANDING PAGE COMPONENT
═══════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const mepCategories = [
    { title: 'Plumbing Systems',   subtitle: 'MEP · Water', image: '/media/category_plumbing.png',     link: '/products?search=plumbing' },
    { title: 'Electrical',         subtitle: 'MEP · Power',  image: '/media/category_electrical.png',  link: '/products?search=electrical' },
    { title: 'Sanitaryware',       subtitle: 'MEP · Bath',   image: '/media/category_sanitaryware.png', link: '/products?search=sanitaryware' },
    { title: 'Indoor Lighting',    subtitle: 'MEP · Light',  image: '/media/category_lighting.png',    link: '/products?search=lighting' },
    { title: 'Outdoor Lighting',   subtitle: 'MEP · Ext.',   image: '/media/category_outdoor_lighting.png', link: '/products?search=outdoor' },
  ];

  const toolsCategories = [
    { title: 'Power Tools',        subtitle: 'Precision Engineering', image: '/media/category_power_tools.png',  link: '/products?search=power+tools' },
    { title: 'Hardware & Fixings', subtitle: 'Heavy Duty',            image: '/media/category_hardware.png',     link: '/products?search=hardware' },
    { title: 'Automation',         subtitle: 'Smart Technology',      image: '/media/category_automation.png',   link: '/products?search=automation' },
    { title: 'Bath Fittings',      subtitle: 'Luxury',                image: '/media/category_bath_fittings.png', link: '/products?search=bath' },
    { title: 'Safety Equipment',   subtitle: 'Protection',            image: '/media/category_safety.png',       link: '/products?search=safety' },
  ];

  const isMobile = useIsMobile();

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: WHITE, color: '#1a1a1a', overflowX: 'hidden' }}>
      <Meta
        title="Grand Palace — Premium MEP & Industrial Solutions, Dubai"
        description="Grand Palace General Trading: MEP, safety, tools & hardware for construction in Dubai and the UAE. 10,000+ products from 50+ trusted brands."
      />

      {/* Google Fonts */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,200;0,300;0,400;1,200;1,300;1,400&family=Inter:wght@300;400;500;600;700&display=swap" />

      {/* Global keyframes */}
      <style>{`
        @keyframes heroSlideIn {
          from { opacity: 0; transform: translateY(36px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.2; transform: scaleY(0.5); }
          50% { opacity: 0.9; transform: scaleY(1); }
        }
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        ::selection { background: ${GOLD}40; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${DARK}; }
        ::-webkit-scrollbar-thumb { background: ${GOLD}60; border-radius: 2px; }
        /* Hide horizontal scrollbar in mobile carousels */
        .mobile-scroll::-webkit-scrollbar { display: none; }
        .mobile-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ══ Navbar ══ */}
      <LandingNav />

      {/* Spacer for fixed navbar */}
      <div style={{ height: isMobile ? 60 : 108 }} />

      {/* ══ 1. HERO CAROUSEL ══ */}
      <HeroSection />

      {/* ══ 2. BRAND TICKER ══ */}
      <BrandMarquee />

      {/* ══ 3. IDENTITY SPLIT ══ */}
      <IdentitySection />

      {/* ══ 4. STATS BAND ══ */}
      <StatsBand />

      {/* ══ 5. MEP COLLECTION GRID ══ */}
      <CollectionsGrid
        eyebrow="Mechanical · Electrical · Plumbing"
        title={<>The MEP<br /><em style={{ fontStyle: 'italic' }}>Collection.</em></>}
        dark={false}
        categories={mepCategories}
        reversed={false}
      />

      {/* ══ 6. QUALITY EDITORIAL BANNER ══ */}
      <EditorialBanner
        image="/media/hero_brand_story.png"
        eyebrow="ISO Certified Excellence"
        headline="Uncompromising<br /><em style='font-style:italic'>Quality Standards.</em>"
        body="Every product in our catalogue meets or exceeds international quality certifications. We partner only with manufacturers who share our commitment to engineering excellence."
        cta="Browse Catalogue"
        ctaLink="/products"
      />

      {/* ══ 7. TOOLS & HARDWARE GRID ══ */}
      <CollectionsGrid
        eyebrow="Precision & Power"
        title={<>Tools &<br /><em style={{ fontStyle: 'italic', color: GOLD }}>Hardware.</em></>}
        dark={true}
        categories={toolsCategories}
        reversed={true}
      />

      {/* ══ 8. WHY CHOOSE US ══ */}
      <WhyUsSection />

      {/* ══ 9. BRANDS SHOWCASE ══ */}
      <BrandsSection />

      {/* ══ 10. CTA ══ */}
      <CTASection />

      {/* ══ FOOTER ══ */}
      <Footer />

      {/* ══ FLOATING ══ */}
      <FloatingWidgets />
    </div>
  );
};

export default LandingPage;
